import { useState, useEffect, useRef } from "react";
import Logo from "../../assets/NWLogo.png";
import { motion } from "framer-motion";
import Button from "../Shared/Button";
import { Link } from "react-router-dom";
import ImgPlaceholder from "../../assets/ImgPlaceholder.svg";
import Dropdown from "./Dropdown";
import { useUserDataStore } from "../../stores/user";
import HamburgerMenu from "../Icons/HamburgerMenu";
import LogoutIcon from "../Icons/LogoutIcon";
import ProfileIcon from "../Icons/ProfileIcon";
import SettingsIcon from "../Icons/SettingsIcon";
import Cookies from "js-cookie";
import { useQuery } from "@tanstack/react-query";
import { GetPinnedAnnouncement } from "../../api/Announcement/GetPinnedAnnoucement";
import NotificationsDropdown from "../Shared/NotificationsDropdown";
import CartDropdown from "../Shared/CartDropdown";
import { getUserProfile } from "../../api/Students/UserProfile";
import Spinner from "../Shared/Spinner";
import { ArrowRight, CircleX, ChevronDown } from "lucide-react";
import { useAnnouncementHeight } from "../../hooks/useAnnouncementHeight";
import { GetAllCategories } from "../../api/Categories/GetAllCategories";
const Navbar = () => {
  const [isClosed, setIsClosed] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [countdown, setCountdown] = useState<{
    [key: string]: {
      days: number;
      hours: number;
      minutes: number;
      seconds: number;
    };
  }>({});
  const isLoggedIn = Cookies.get("accessToken");
  const { userData, reset } = useUserDataStore();
  const {
    data: PinnedAnnouncementsResponse,
    isLoading: isPinnedAnnouncementLoading,
  } = useQuery({
    queryFn: GetPinnedAnnouncement,
    queryKey: ["pinnedAnnouncements"],
    staleTime: 0,
    refetchInterval: 0,
  });
  const { data: categories, isLoading: isCategoriesLoading } = useQuery({
    queryFn: GetAllCategories,
    queryKey: ["categories"],
    staleTime: 0,
    refetchInterval: 0,
  });
  const courseCategories = categories?.data || [];
  const { data: StudentProfile, isLoading: isStudentProfileLoading } = useQuery(
    {
      queryFn: getUserProfile,
      queryKey: ["userprofile"],
      staleTime: 0,
      refetchInterval: 0,
      enabled: !!Cookies.get("accessToken") && !userData?.firstName,
    }
  );
  const student = StudentProfile?.data;
  useEffect(() => {
    if (student) {
      useUserDataStore.getState().setUserData({
        ...student,
        role: "student",
      });
    }
  }, [student]);

  const pinnedAnnouncements =
    PinnedAnnouncementsResponse?.data?.AnnouncementData || [];

  const handleLogout = () => {
    Cookies.remove("accessToken");
    reset();
  };

  const toggleHeader = () => {
    setIsClosed(!isClosed);
  };
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };
  const sidebarVariants = {
    open: { x: 0 },
    closed: { x: "100%" },
  };
  const [slideOver, setSlideOver] = useState<{
    [key: string]: boolean;
  }>({
    courses: false,
  });
  const handleOpenSlide = (item: string) => {
    setSlideOver((prev) =>
      Object.fromEntries(Object.keys(prev).map((key) => [key, key === item]))
    );
  };

  const resetSlideOver = () =>
    setSlideOver({
      courses: false,
    });

  const [isCoursesOpen, setIsCoursesOpen] = useState(false);

  const calculateTimeLeft = (createdAt: string, deadline: string) => {
    // Convert createdAt to a Date object
    const createdTime = new Date(createdAt).getTime();

    // Parse the deadline (HH:MM:SS) into hours, minutes, and seconds
    const [hours, minutes, seconds] = deadline.split(":").map(Number);

    // Convert the deadline into milliseconds
    const deadlineInMilliseconds =
      hours * 60 * 60 * 1000 + minutes * 60 * 1000 + seconds * 1000;

    // Calculate the actual deadline time by adding the deadline milliseconds to createdAt time
    const actualDeadlineTime = createdTime + deadlineInMilliseconds;

    // Get the current time
    const currentTime = new Date().getTime();

    // Calculate the difference between the deadline and the current time
    const difference = actualDeadlineTime - currentTime;

    // If the time has passed, return zeros
    if (difference <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    // Return the calculated time left in days, hours, minutes, and seconds
    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor(
        (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      ),
      minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((difference % (1000 * 60)) / 1000),
    };
  };

  useEffect(() => {
    const intervals: { [key: string]: NodeJS.Timeout } = {};

    pinnedAnnouncements.forEach((announcement) => {
      if (announcement.deadline && announcement.createdAt) {
        intervals[announcement.id] = setInterval(() => {
          setCountdown((prev) => ({
            ...prev,
            [announcement.id]: calculateTimeLeft(
              announcement.createdAt,
              announcement.deadline
            ),
          }));
        }, 1000);
      }
    });

    return () => {
      Object.values(intervals).forEach(clearInterval);
    };
  }, [pinnedAnnouncements]);

  const announcementRef = useRef<HTMLDivElement>(null);
  const setAnnouncementHeight = useAnnouncementHeight(
    (state) => state.setHeight
  );

  useEffect(() => {
    if (
      announcementRef.current &&
      pinnedAnnouncements.length > 0 &&
      !isClosed
    ) {
      const height = announcementRef.current.offsetHeight;
      setAnnouncementHeight(height);
    } else {
      setAnnouncementHeight(0);
    }
  }, [pinnedAnnouncements.length, isClosed, setAnnouncementHeight]);

  return (
    <div onMouseLeave={() => resetSlideOver()}>
      <div
        key="courses"
        style={{
          boxShadow:
            "rgba(50, 50, 93, 0.25) 0px 2px 5px -1px, rgba(0, 0, 0, 0.3) 0px 1px 3px -1px",
        }}
        className={`w-full z-[999] xl:-top-[710px] -top-[750px] transition-all xl:block hidden duration-700 ${
          slideOver.courses && "!top-[0] "
        }  min-h-[650px] bg-white  fixed left-1/2 -translate-x-1/2   border-b-white border-b border-opacity-20`}
      >
        <div className="container mx-auto py-16">
          <div className="relative">
            <div className="mt-40">
              <h1 className="text-primary font-semibold mb-4">
                Courses Categories
              </h1>
              <div className="grid grid-cols-3 gap-y-3 gap-x-5">
                {isCategoriesLoading && <Spinner />}
                {courseCategories.map((category, index) => (
                  <Link
                    to={`/courses?category=${category.name}`}
                    key={index}
                    className={`flex items-center gap-1 text-textPrimary hover:text-secondary hover:translate-x-3 transition-all duration-300 cursor-pointer `}
                  >
                    <ArrowRight size={16} />
                    <h1 className="text-sm ">{category.name}</h1>
                  </Link>
                ))}
              </div>
            </div>
          </div>
          <Link to={"/courses"}>
            <Button
              onClick={() => resetSlideOver()}
              variant="primary"
              className="w-[160px] mt-12 !h-12 !text-sm"
            >
              View More <ArrowRight size={20} />
            </Button>
          </Link>
        </div>
      </div>

      <nav className="w-full flex flex-col justify-center bg-white fixed top-0 z-[99999999999999] border-[#121212]/30 border-b">
        {pinnedAnnouncements.length > 0 && (
          <>
            {pinnedAnnouncements.map((announcement) => (
              <header
                ref={announcementRef}
                className={`bg-secondary transition-all z-50 opacity-100  relative duration-300 ease-in-out ${
                  isClosed
                    ? "!h-0 p-0 !opacity-0 "
                    : "md:min-h-24 min-h-[72px] py-3 px-4"
                } `}
              >
                <div
                  className={`md:absolute font-bold sm:right-3 right-2 sm:top-3 -top-6 justify-end sm:mb-2 mb-1 flex`}
                >
                  <button onClick={toggleHeader}>
                    <CircleX className="sm:size-auto size-4 text-white" />
                  </button>
                </div>
                {announcement.deadline && (
                  <div className="text-center w-full mb-1.5">
                    <p className=" text-white font-bold md:text-lg text-xs ">
                      Time left: {countdown[announcement.id]?.days}d{" "}
                      {countdown[announcement.id]?.hours}h{" "}
                      {countdown[announcement.id]?.minutes}m{" "}
                      {countdown[announcement.id]?.seconds}s
                    </p>
                  </div>
                )}

                <div
                  className={`flex justify-center  items-center sm:gap-1.5 container sm:flex-row flex-col relative h-full `}
                >
                  {isPinnedAnnouncementLoading && (
                    <h1 className=" text-white font-bold md:text-base text-xs">
                      Loading Announcement...
                    </h1>
                  )}

                  <h1
                    key={announcement.id}
                    className=" text-white font-bold lg:text-[22px] md:text-xl text-xs text-center max-w-[1200px]"
                  >
                    {announcement.message}
                    <Link
                      target="_blank"
                      className="text-primary font-bold md:text-xl text-xs mx-2"
                      to={announcement.forward_link}
                    >
                      {announcement.forward_text}
                    </Link>
                  </h1>
                </div>
              </header>
            ))}
          </>
        )}
        <div className="flex justify-between container items-center md:h-20 h-[60px] relative z-50">
          <div>
            <Link className="flex items-center" to="/">
              <img src={Logo} className=" md:w-16 h-auto w-12" />
              <span className="md:text-lg text-xs text-burntBlack font-bold"></span>
            </Link>
          </div>
          <div className="xl:hidden flex gap-1 items-center flex-row-reverse">
            <button onClick={toggleSidebar}>
              <HamburgerMenu />
            </button>
            <NotificationsDropdown />
            <CartDropdown />
          </div>
          {isSidebarOpen && (
            <motion.div
              className="fixed inset-0 bg-black bg-opacity-50 z-40 xl:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={toggleSidebar}
            />
          )}
          <motion.div
            className="flex flex-col bg-white fixed top-0 right-0 h-full shadow-lg z-[9999999999] p-5 xl:hidden md:w-[50%] w-[70%]"
            variants={sidebarVariants}
            initial="closed"
            animate={isSidebarOpen ? "open" : "closed"}
            transition={{
              ease: "linear",
              duration: 2,
              x: { duration: 0.3 },
            }}
          >
            <button onClick={toggleSidebar} className="self-end">
              <CircleX />
            </button>
            {isLoggedIn && (
              <>
                {isStudentProfileLoading || !userData?.firstName ? (
                  <Spinner />
                ) : (
                  <div className="w-full border-b border-black/5 ">
                    <div className="flex items-center gap-3">
                      <img
                        src={userData?.img_url ?? ImgPlaceholder}
                        className="rounded-full size-14 object-cover border border-black/20"
                        alt=""
                      />
                      <div>
                        <h1 className="font-semibold text-burntBlack mb-0.5">
                          {userData?.firstName} {userData?.lastName}
                        </h1>
                        <p className="text-lightGray text-xs">
                          {userData?.email}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-6 py-5">
                      <Link
                        to="/settings"
                        className="flex gap-2 cursor-pointer"
                      >
                        <SettingsIcon />
                        <p className="text-sm text-black">Settings</p>
                      </Link>
                      {userData?.role === "student" && (
                        <div className="flex gap-2 cursor-pointer">
                          <ProfileIcon />
                          <Link to="/my-profile" className="text-sm text-black">
                            My Profile
                          </Link>
                        </div>
                      )}
                      <button onClick={handleLogout} className="flex gap-2">
                        <LogoutIcon />
                        <p className="text-sm text-[#FF0000]">Log Out</p>
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            <Link className="p-2" to="/">
              Home
            </Link>
            <Link className="p-2" to="/about-us">
              About Us
            </Link>
            <div className="flex flex-col">
              <button
                onClick={() => setIsCoursesOpen(!isCoursesOpen)}
                className="p-2 flex items-center justify-between w-full"
              >
                <span>Courses</span>
                <ChevronDown
                  className={`transition-transform duration-200 ${
                    isCoursesOpen ? "rotate-180" : ""
                  }`}
                  size={20}
                />
              </button>

              {isCoursesOpen && (
                <div className="ml-4 flex flex-col space-y-2 py-2">
                  {isCategoriesLoading ? (
                    <Spinner />
                  ) : (
                    courseCategories.map((category, index) => (
                      <Link
                        key={index}
                        to={`/courses?category=${category.name}`}
                        className="p-2 text-sm text-gray-600 hover:text-primary"
                        onClick={toggleSidebar}
                      >
                        {category.name}
                      </Link>
                    ))
                  )}
                </div>
              )}
            </div>
            <Link className="p-2" to="/books">
              Books
            </Link>

            <Link className="p-2" to="/faqs">
              FAQs
            </Link>
            <Link className="p-2" to="/contact-us">
              Contact Us
            </Link>
            <Link className="p-2" to="/training">
              Training
            </Link>
            <Link className="p-2" to="/consultation">
              Consultation
            </Link>

            {/* <Link className="p-2" to="/consultation">Consultation</Link> */}
            <div className="pt-2">
              {userData?.role === "admin" && (
                <Link
                  to={`/dashboard/students`}
                  className="font-bold text-slate mt-5 w-full"
                >
                  <Button
                    variant="primary"
                    className="w-full mx-auto !text-sm !rounded !h-[42px]"
                  >
                    Dashboard{" "}
                  </Button>
                </Link>
              )}

              {userData?.role === "sales" && (
                <Link
                  to={`/sales/dashboard/students`}
                  className="font-bold text-slate mt-5 w-full"
                >
                  <Button
                    variant="primary"
                    className="w-full mx-auto !text-sm !rounded !h-[42px]"
                  >
                    Dashboard{" "}
                  </Button>
                </Link>
              )}

              {!isLoggedIn && (
                <>
                  <Link to="/login">
                    {" "}
                    <Button
                      variant="transparent"
                      className="rounded-[12px] !h-11 w-full md:mb-5 mb-3 !text-lg"
                    >
                      Log In
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button
                      variant="primary"
                      className="rounded-[12px] !h-11 w-full !text-lg"
                    >
                      Sign Up
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </motion.div>

          <div className="hidden gap-8 xl:flex">
            <Link
              className="p-1 font-medium hover:text-primary text-burntBlack duration-300 ease-in-out transition-all"
              to="/"
            >
              Home
            </Link>
            <Link
              className="p-1 font-medium hover:text-primary text-burntBlack duration-300 ease-in-out transition-all"
              to="/about-us"
            >
              About Us
            </Link>
            <Link
              onMouseEnter={() => handleOpenSlide("courses")}
              className="p-1 font-medium hover:text-primary text-burntBlack duration-300 ease-in-out transition-all"
              to="/courses"
            >
              Courses
            </Link>
            <Link
              className="p-1 font-medium hover:text-primary text-burntBlack duration-300 ease-in-out transition-all"
              to="/books"
            >
              Books
            </Link>
            <Link
              className="p-1 font-medium hover:text-primary text-burntBlack duration-300 ease-in-out transition-all"
              to="/faqs"
            >
              FAQs
            </Link>
            <Link
              className="p-1 font-medium hover:text-primary text-burntBlack duration-300 ease-in-out transition-all"
              to="/contact-us"
            >
              Contact Us
            </Link>
            <Link
              className="p-1 font-medium hover:text-primary text-burntBlack duration-300 ease-in-out transition-all"
              to="/training"
            >
              Training
            </Link>

            <Link
              className="p-1 font-medium hover:text-primary text-burntBlack duration-300 ease-in-out transition-all"
              to="/consultation"
            >
              Consultation
            </Link>
          </div>
          <div className="hidden gap-4 xl:flex">
            {isLoggedIn ? (
              <div className="flex items-center gap-4">
                {isStudentProfileLoading || !userData?.firstName ? (
                  <Spinner />
                ) : (
                  <>
                    <NotificationsDropdown />
                    <CartDropdown />
                    <Dropdown>
                      <img
                        className="size-[50px] object-cover rounded-full"
                        src={userData?.img_url ?? ImgPlaceholder}
                        alt=""
                      />
                    </Dropdown>
                  </>
                )}
              </div>
            ) : (
              <>
                <Link to="/login">
                  {" "}
                  <Button
                    variant="transparent"
                    className="rounded-[12px] !h-11 w-[120px] !text-lg"
                  >
                    Log In
                  </Button>
                </Link>
                <Link to="/register">
                  <Button
                    variant="primary"
                    className="rounded-[12px] !h-11 w-[120px] !text-lg"
                  >
                    Sign Up
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>
    </div>
  );
};

export default Navbar;
